import { Cross2Icon, InfoCircledIcon } from '@radix-ui/react-icons';
import { Button, Popover, Skeleton } from '@radix-ui/themes';
import classNames from 'classnames';
import type { FitPlayerProfileResponse, MatchPreview } from 'api/generated';
import { isCurrentUserAdmin } from 'helpers/authentication';
import { useWindowSize } from 'helpers/useWindowSize';
import type { PlayerMatchType } from 'types/player';
import PlayerUserObject from './PlayerUserObject';
import UpdateMatchResultModal from './UpdateMatchResultModal';

export const MatchCardObject: React.FC<
  Partial<MatchPreview> & {
    is_deletable?: boolean;
    is_editable?: boolean;
    type?: string;
    fit_points?: number;
    calculated_fit_points?: number;
    is_in_best_matches?: boolean;
    partner?: PlayerMatchType | null;
    loading: boolean;
    player: Partial<FitPlayerProfileResponse>;
    hideFitPoints?: boolean;
    showClub?: boolean;
    isSuperSimulation?: boolean;
    forceMobile?: boolean;
    onDeleteMatch?: (match: { is_deletable: boolean; id: number }) => void;
    editMatchResult?: (match: { is_editable: boolean; id: number; type: string }) => void;
    expandInMatchCard?: boolean;
    isPlayerSimulatedHigher?: boolean;
    isUserMatch?: boolean;
  }
> = ({
  id,
  is_deletable,
  is_editable,
  type,
  loading,
  fit_points,
  calculated_fit_points,
  is_in_best_matches,
  has_won,
  result,
  first_opponent,
  second_opponent,
  partner,
  player,
  hideFitPoints,
  showClub = false,
  isSuperSimulation,
  forceMobile,
  onDeleteMatch,
  editMatchResult,
  expandInMatchCard = false,
  isPlayerSimulatedHigher,
  isUserMatch = false,
}) => {
  const isMatchWithoutWinner = has_won === undefined || has_won === null;
  const showIcon = calculated_fit_points !== undefined ? (calculated_fit_points < fit_points! ? true : false) : false;
  const pointsToShow = calculated_fit_points !== undefined ? calculated_fit_points : fit_points;
  const size = useWindowSize();
  const stretch = size?.width < 450;

  const containerClassName = classNames(
    'w-full',
    'flex flex-col items-start',
    'rounded-lg',
    'border border-solid border-neutral-neutral-3',
    { 'bg-semantic-error-2': has_won === false },
    { 'bg-accent-accent-1': has_won === true },
    { 'bg-neutral-neutral-1': isMatchWithoutWinner },
    { 'ring-2 ring-iris-9': isUserMatch },
  );

  const pointsClassName = classNames(
    'flex justify-end items-center',
    'px-1.5 py-0.5 gap-1.5 rounded-xs',
    'text-xs font-medium tracking-wide',
    { 'text-semantic-error-alpha-11 bg-semantic-error-alpha-3': has_won === false },
    { 'text-accent-accent-alpha-11 bg-accent-accent-alpha-3 line-through': has_won === true && !is_in_best_matches },
    { 'text-accent-accent-6 bg-neutral-neutral-9': is_in_best_matches && !isSuperSimulation },
    { 'text-white bg-iris-9': is_in_best_matches && isSuperSimulation },
  );

  const firstPlayerClassName = classNames(
    'flex flex-col items-start flex-1 min-w-0', // Add min-w-0 here
    'w-full',
    { 'bg-semantic-error-3': has_won === false },
    { 'bg-accent-accent-3': has_won === true },
    'font-bold',
  );

  const secondPlayerClassName = classNames(
    'flex flex-col items-start flex-1 min-w-0', // Add min-w-0 here
    'w-full',
    { 'bg-semantic-error-2': has_won === false },
    { 'bg-accent-accent-2': has_won === true },
  );

  const compact = second_opponent ? true : false;
  const currentPlayer = (
    <PlayerUserObject
      {...player}
      province={player?.club?.province}
      region={player?.club?.region}
      club_name={player?.club?.name}
      user_exists={player?.user_id !== null}
      linkToPlayer={true}
      showClub={showClub}
      compact={compact}
      expandInMatchCard={expandInMatchCard}
      stretch={stretch ? size.width : undefined}
      shrinkForComments={size.width < 375}
      simulated_rank_higher={isPlayerSimulatedHigher}
      type='fit_player_profile'
    />
  );

  const firstOpponent = first_opponent && (
    <PlayerUserObject
      {...first_opponent}
      province={first_opponent?.club?.province}
      region={first_opponent?.club?.region}
      club_name={first_opponent?.club?.name}
      linkToPlayer={true}
      showClub={showClub}
      compact={compact}
      expandInMatchCard={expandInMatchCard}
      isSuperSimulation={isSuperSimulation}
      stretch={stretch ? size.width : undefined}
      shrinkForComments={size.width < 375}
    />
  );

  let secondOpponent = null;
  let partnerPlayer = null;
  if (second_opponent) {
    secondOpponent = (
      <PlayerUserObject
        {...second_opponent}
        province={second_opponent?.club?.province}
        region={second_opponent?.club?.region}
        club_name={second_opponent?.club?.name}
        linkToPlayer={true}
        showClub={showClub}
        compact={compact}
        expandInMatchCard={expandInMatchCard}
        isSuperSimulation={isSuperSimulation}
        stretch={stretch ? size.width : undefined}
        shrinkForComments={size.width < 375}
      />
    );

    partnerPlayer = partner && (
      <PlayerUserObject
        {...partner}
        province={partner?.club?.province}
        region={partner?.club?.region}
        club_name={partner?.club?.name}
        linkToPlayer={true}
        showClub={showClub}
        compact={compact}
        expandInMatchCard={expandInMatchCard}
        isSuperSimulation={isSuperSimulation}
        stretch={stretch ? size.width : undefined}
        shrinkForComments={size.width < 375}
      />
    );
  }

  return (
    <div className={containerClassName}>
      <div
        id='header'
        className='border-b-solid border-b-neutral-neutral-3 flex items-center justify-between gap-2 self-stretch border-b bg-inherit px-3 py-2'
      >
        <Skeleton loading={loading} className='h-6 w-6'>
          {isMatchWithoutWinner ? (
            <div className='text-neutral-neutral-10 truncate text-xs font-medium tracking-wide' />
          ) : has_won ? (
            <div className='bg-accent-accent-8 text-accent-accent-1 flex h-6 w-6 items-center justify-center gap-2 rounded-xl text-xs font-bold'>
              W
            </div>
          ) : (
            <div className='bg-red-9 text-red-1 flex h-6 w-6 items-center justify-center gap-2 rounded-xl text-xs font-bold'>
              L
            </div>
          )}
        </Skeleton>
        <Skeleton loading={loading} className='h-6 w-auto'>
          {!(isMatchWithoutWinner || fit_points === undefined) && !hideFitPoints && (
            <div className={pointsClassName}>
              {showIcon && (
                <div id='icon' className='flex justify-end py-1'>
                  <Popover.Root>
                    <Popover.Trigger>
                      <InfoCircledIcon width='12' height='12' className='cursor-pointer' />
                    </Popover.Trigger>
                    <Popover.Content maxWidth='300px' className='bg-neutral-neutral-11'>
                      <div className='text-accent-accent-6 text-xs'>
                        I punti dati da questo match di torneo rodeo sono stati ridotti per il metodo di calcolo
                        dell'anno in corso. Il tasto 'Spiega Classifica' aiuta a capire meglio il calcolo e le soglie.
                      </div>
                    </Popover.Content>
                  </Popover.Root>
                </div>
              )}
              {!showIcon && is_in_best_matches === false && has_won && (
                <div id='icon' className='flex justify-end py-1'>
                  <Popover.Root>
                    <Popover.Trigger>
                      <InfoCircledIcon width='12' height='12' className='cursor-pointer' />
                    </Popover.Trigger>
                    <Popover.Content maxWidth='300px' className='bg-neutral-neutral-11'>
                      <div className='text-accent-accent-6 text-xs'>
                        I punti di questo match non sono considerati nel calcolo. Il tasto 'Spiega Classifica' aiuta a
                        capire il perchè
                      </div>
                    </Popover.Content>
                  </Popover.Root>
                </div>
              )}
              {pointsToShow} punti
            </div>
          )}
        </Skeleton>
      </div>
      <div className='bg-neutral-neutral-3 flex h-px self-stretch' />
      <div
        id='match'
        className={classNames('flex max-w-full flex-col items-start self-stretch', { 'md:flex-row': !forceMobile })}
      >
        <div
          id='players'
          className='border-r-solid border-r-neutral-neutral-3 flex max-w-full min-w-0 flex-1 flex-col items-start self-stretch border-r' // Add min-w-0 here
        >
          <Skeleton loading={loading} className='h-auto w-auto'>
            <div className={firstPlayerClassName}>{has_won === false ? firstOpponent : currentPlayer}</div>
            {partnerPlayer && (
              <div className={firstPlayerClassName}>{has_won === false ? secondOpponent : partnerPlayer}</div>
            )}
          </Skeleton>
          <div className='bg-neutral-neutral-3 flex h-px self-stretch' />
          <Skeleton loading={loading} className='h-auto w-auto'>
            <div className={secondPlayerClassName}>{has_won === false ? currentPlayer : firstOpponent}</div>
            {partnerPlayer && (
              <div className={secondPlayerClassName}>{has_won === false ? partnerPlayer : secondOpponent}</div>
            )}
          </Skeleton>
        </div>
        <Skeleton loading={loading} className='h-auto w-auto'>
          <div
            id='result'
            className={classNames(
              'flex w-auto min-w-16 flex-col items-center justify-center gap-3 self-stretch text-nowrap',
              'border-neutral-neutral-3 border-0 border-t border-solid px-3 py-2',
              'text-neutral-neutral-11 bg-inherit text-lg leading-7 font-medium tracking-tight',
              { 'md:border-t-0 md:border-l': !forceMobile },
            )}
          >
            {result}

            {isCurrentUserAdmin() && editMatchResult && (
              <UpdateMatchResultModal
                editMatchResult={() => editMatchResult({ is_editable: is_editable!, id: id!, type: type! })}
              />
            )}
            {isCurrentUserAdmin() && onDeleteMatch && (
              <Button
                size='2'
                variant='ghost'
                className='bg-accent-accent-6 flex cursor-pointer items-center self-stretch'
                onClick={() => onDeleteMatch({ is_deletable: is_deletable!, id: id! })}
              >
                <Cross2Icon />
              </Button>
            )}
          </div>
        </Skeleton>
      </div>
    </div>
  );
};

export default MatchCardObject;
